import { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import PaymentForm from '../components/PaymentForm';
import PaymentDetail from '../components/PaymentDetail';
import ImportModal from '../components/ImportModal';
import ColumnSettings from '../components/ColumnSettings';
import DataTable from '../components/DataTable';
import RequestsSummary from '../components/RequestsSummary';
import PeriodPicker from '../components/PeriodPicker';
import Combobox from '../components/Combobox';

const STATUS_LABELS = { new: 'Новая', pending: 'На подписи', paid: 'Оплачена', rejected: 'Отклонена' };
const ALL_ROWS_LIMIT = 20000;
const ALLOWED_STATUS_TARGETS = { accountant: ['pending'], admin: ['paid', 'rejected', 'new', 'pending'] };

const COLUMNS = [
  { key: 'id', label: '#' },
  { key: 'creator_name', label: 'Создатель' },
  { key: 'doc_type', label: 'Тип' },
  { key: 'project', label: 'Проект' },
  { key: 'direction', label: 'Направление' },
  { key: 'article', label: 'Статья' },
  { key: 'recipient', label: 'Получатель' },
  { key: 'amount', label: 'Сумма' },
  { key: 'source', label: 'Источник' },
  { key: 'comment', label: 'Комментарий' },
  { key: 'status', label: 'Статус' },
  { key: 'created_at', label: 'Создана' },
];

function BulkEditPanel({ meta, selected, role, onClose, onDone }) {
  const [direction, setDirection] = useState('');
  const [article, setArticle] = useState('');
  const [project, setProject] = useState('');
  const [status, setStatus] = useState('');
  const [paidDate, setPaidDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const statusTargets = ALLOWED_STATUS_TARGETS[role] || [];

  const articlesForDirection = direction
    ? meta.articles.filter(a => meta.directions.find(d => d.id === a.direction_id)?.name === direction)
    : meta.articles;

  const apply = async () => {
    setError('');
    const payload = { ids: selected };
    if (direction !== '') payload.direction = direction;
    if (article !== '') payload.article = article;
    if (project !== '') payload.project = project;
    if (status !== '') payload.status = status;
    if (status === 'paid') payload.date_paid = paidDate;
    if (payload.direction === undefined && payload.article === undefined && payload.project === undefined && payload.status === undefined) {
      setError('Укажите, что изменить');
      return;
    }
    setSaving(true);
    try {
      await api.patch('/payments', payload);
      onDone();
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка сохранения');
    } finally { setSaving(false); }
  };

  return (
    <div className="toolbar" style={{ background: '#f5f5f5', padding: 8, borderRadius: 6, marginBottom: 8 }}>
      <span>Выбрано: {selected.length}</span>
      <select value={direction} onChange={e => { setDirection(e.target.value); setArticle(''); }}>
        <option value="">Направление — не менять</option>
        {meta.directions.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
      </select>
      <div style={{ width: 240 }}>
        <Combobox value={article} onChange={setArticle} options={articlesForDirection.map(a => ({ value: a.name, label: a.name }))} placeholder="Статья — не менять" />
      </div>
      <div style={{ width: 200 }}>
        <Combobox value={project} onChange={setProject} options={meta.projects.map(p => ({ value: p, label: p }))} placeholder="Проект — не менять" />
      </div>
      {statusTargets.length > 0 && (
        <select value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">Статус — не менять</option>
          {statusTargets.map(s => <option key={s} value={s}>→ {STATUS_LABELS[s]}</option>)}
        </select>
      )}
      {status === 'paid' && (
        <input type="date" value={paidDate} onChange={e => setPaidDate(e.target.value)} title="Дата фактической оплаты" />
      )}
      {error && <span className="error-msg">{error}</span>}
      <button className="btn-primary btn-sm" disabled={saving} onClick={apply}>{saving ? 'Сохранение...' : 'Применить'}</button>
      <button className="btn-secondary btn-sm" onClick={onClose}>Отмена</button>
    </div>
  );
}

const loadVisible = () => {
  try {
    const saved = JSON.parse(localStorage.getItem('paymentsColumns'));
    if (saved) return saved;
  } catch {}
  return { recipient: false };
};

export default function Payments() {
  const { user } = useAuth();
  const { id: idFromUrl } = useParams();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  // DataTable хранит выбор чекбоксов и свои внутренние фильтры по столбцам в собственном
  // состоянии — если убирать её из дерева на каждую перезагрузку (например, после смены
  // статуса заявки), это состояние сбрасывается и выделение/фильтр по проекту слетает.
  // Поэтому после первой успешной загрузки таблицу больше не размонтируем.
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [filters, setFilters] = useState({ status: '', doc_type: '', direction: '', project: '' });
  const [period, setPeriod] = useState({ from: null, to: null });
  const [visible, setVisible] = useState(loadVisible);
  const [selected, setSelected] = useState([]);
  const [deleteError, setDeleteError] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [openId, setOpenId] = useState(idFromUrl ? Number(idFromUrl) : null);

  const canBulkEdit = user.role === 'admin' || user.role === 'accountant';
  const canExport = ['admin', 'accountant', 'rop'].includes(user.role);

  // Прямая ссылка на заявку (например, из письма) — /payments/:id открывает карточку сразу,
  // без необходимости искать заявку в общем списке.
  useEffect(() => { if (idFromUrl) setOpenId(Number(idFromUrl)); }, [idFromUrl]);

  const closeDetail = () => { setOpenId(null); if (idFromUrl) navigate('/payments', { replace: true }); };

  useEffect(() => { api.get('/payments/meta').then(r => setMeta(r.data)); }, []);

  const load = useCallback(async () => {
    try {
      const params = { ...filters, date_from: period.from, date_to: period.to, page: 1, limit: ALL_ROWS_LIMIT };
      Object.keys(params).forEach(k => (params[k] === '' || params[k] == null) && delete params[k]);
      const { data } = await api.get('/payments', { params });
      setRows(data.data);
      setTotal(data.total);
    } finally {
      setLoadedOnce(true);
    }
  }, [filters, period]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => { localStorage.setItem('paymentsColumns', JSON.stringify(visible)); }, [visible]);

  const setFilter = (key, value) => setFilters(f => ({ ...f, [key]: value }));

  const deleteSelected = async () => {
    if (!selected.length || deleting) return;
    setDeleteError('');
    if (!window.confirm(`Удалить ${selected.length} заявок?`)) return;
    setDeleting(true);
    try {
      await api.delete('/payments', { data: { ids: selected } });
      setSelected([]);
      await load();
    } catch (err) {
      setDeleteError(err.response?.data?.error || 'Не удалось удалить заявки. Попробуйте ещё раз.');
    } finally {
      setDeleting(false);
    }
  };

  const fmtCell = (row, key) => {
    if (key === 'amount') return `${parseFloat(row.amount).toLocaleString('ru')} ₽`;
    if (key === 'status') return <span className={`badge badge-${row.status}`}>{STATUS_LABELS[row.status]}</span>;
    if (key === 'doc_type') return row.doc_type === 'fact' ? 'Факт' : 'Заявка';
    if (key === 'created_at') return new Date(row.created_at).toLocaleDateString('ru');
    if (key === 'project') {
      const to = row.project_id ? `/projects/deal/${row.project_id}` : `/projects/${encodeURIComponent(row.project)}`;
      return <Link to={to} onClick={e => e.stopPropagation()}>{row.project}</Link>;
    }
    return row[key] ?? '—';
  };

  const visibleColumns = COLUMNS.filter(c => visible[c.key] !== false);
  const columns = [
    ...visibleColumns.map(c => ({
      key: c.key,
      label: c.label,
      render: row => fmtCell(row, c.key),
      sortValue: c.key === 'amount' ? (row => Number(row.amount)) : c.key === 'created_at' ? (row => row.created_at) : undefined,
      filterValue: c.key === 'status' ? (row => STATUS_LABELS[row.status]) : c.key === 'doc_type' ? (row => (row.doc_type === 'fact' ? 'Факт' : 'Заявка')) : undefined,
      filterable: c.key !== 'amount',
      summable: c.key === 'amount',
      footerFormat: c.key === 'amount' ? (v => `${Math.round(v).toLocaleString('ru')} ₽`) : undefined,
    })),
  ];

  return (
    <div>
      <div className="toolbar">
        <select value={filters.status} onChange={e => setFilter('status', e.target.value)}>
          <option value="">Все статусы</option>
          {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={filters.doc_type} onChange={e => setFilter('doc_type', e.target.value)}>
          <option value="">Все типы</option>
          <option value="request">Заявка</option>
          <option value="fact">Факт</option>
        </select>
        {meta && (
          <select value={filters.direction} onChange={e => setFilter('direction', e.target.value)}>
            <option value="">Все направления</option>
            {meta.directions.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
          </select>
        )}
        {meta && (
          <select value={filters.project} onChange={e => setFilter('project', e.target.value)}>
            <option value="">Все проекты</option>
            {meta.projects.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        )}
        <PeriodPicker onChange={setPeriod} />
        <div className="toolbar-spacer" />
        {canBulkEdit && selected.length > 0 && (
          <button className="btn-secondary btn-sm" onClick={() => setShowBulk(true)}>Изменить выбранные ({selected.length})</button>
        )}
        {user.role === 'admin' && selected.length > 0 && (
          <button className="btn-danger btn-sm" onClick={deleteSelected} disabled={deleting}>{deleting ? 'Удаление…' : `Удалить (${selected.length})`}</button>
        )}
        {user.role === 'admin' && (
          <button className="btn-secondary btn-sm" onClick={() => setShowImport(true)}>Импорт</button>
        )}
        <ColumnSettings columns={COLUMNS} visible={visible} onChange={setVisible} />
        <button className="btn-primary btn-sm" onClick={() => setShowForm(true)}>+ Новая заявка</button>
      </div>

      {deleteError && <div className="error-msg" role="alert">{deleteError}</div>}

      {showBulk && meta && selected.length > 0 && (
        <BulkEditPanel meta={meta} selected={selected} role={user.role} onClose={() => setShowBulk(false)} onDone={() => { setShowBulk(false); setSelected([]); load(); }} />
      )}

      {!loadedOnce ? <div className="loading-text">Загрузка...</div> : (
        <DataTable
          columns={columns}
          rows={rows}
          initialSort={{ key: 'id', dir: 'desc' }}
          onRowClick={row => setOpenId(row.id)}
          emptyText="Заявок не найдено"
          selectedIds={canBulkEdit ? selected : undefined}
          onSelectedChange={canBulkEdit ? setSelected : undefined}
          canExport={canExport}
          exportFilename="Заявки"
          renderSummary={visibleRows => <RequestsSummary rows={visibleRows} />}
        />
      )}
      {loadedOnce && total > rows.length && <div className="text-muted" style={{ fontSize: 12 }}>Показаны первые {rows.length} из {total} — сузьте период или фильтр.</div>}

      {showForm && <PaymentForm onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
      {showImport && <ImportModal onClose={() => setShowImport(false)} onDone={load} />}
      {openId && <PaymentDetail id={openId} onClose={closeDetail} onChanged={load} />}
    </div>
  );
}
